import { DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  EnvironmentProviders,
  ErrorHandler,
  inject,
  ModuleWithProviders,
  NgModule,
  provideAppInitializer,
  Provider,
  Sanitizer,
  Type,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';

import * as Sentry from '@sentry/angular';

import { environment } from '@eai-tdv/environment';

import { LOCAL_STORAGE_KEYS } from '@eai-tdv/core/models';
import {
  ITranslocoRootConfig,
  TRANSLOCO_ROOT_INJECTION_TOKEN,
  TranslocoRootModule,
} from '@eai-tdv/core/transloco';

import { GlobalProgressBarComponent } from '@eai-tdv/shared/global-progress-bar';

import { AppComponent } from './app.component';
import {
  AppInitializerService,
  initializeApp,
} from './app-initializer.service';
import { AppRoutingModule } from './app-routing.module';
import { AppMenuComponent } from './core/components/app-menu/app-menu.component';
import { VersionAvailablePopupComponent } from './core/components/app-version-watch/version-available-popup/version-available-popup.component';
import { LogoutOverlayComponent } from './core/components/logout-overlay/logout-overlay.component';
import { SupportChatComponent } from './core/components/support-chat/support-chat.component';
import { CredentialsInterceptor } from './core/interceptors/credentials.interceptor';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { HttpOfflineRetryInterceptor } from './core/interceptors/http-offline-retry.interceptor';
import { JwtRefreshInterceptor } from './core/interceptors/jwt-refresh.interceptor';
import {
  API_ENDPOINT_INJECTION_TOKEN,
  AppVersionService,
  AuthUserFacadeService,
  AuthUserStateService,
  DICTIONARY_SERVICE_INJECTION_TOKEN,
  ErrorRemoteTrackerService,
  LocalStorageWebService,
  MAT_DATE_PROVIDER,
} from './core/services';
import { ExtraHtmlSanitizerService } from './core/services/extra-html-sanitizer.service';
import { UploadResourceWrapComponent } from './features/resource-upload/upload-resource-wrap.component';
import { ProvideFactoryHelper, ProvideValueHelper } from './shared/functions';
import { CdkTrapFocusModule } from './shared/modules/cdk-focus-trap.module';

const providers: Array<Provider | EnvironmentProviders> = [
  AppVersionService,
  DecimalPipe,
  AuthUserStateService,
  LocalStorageWebService,
  AuthUserFacadeService,
  AppInitializerService,
  ErrorRemoteTrackerService,
  { provide: API_ENDPOINT_INJECTION_TOKEN, useValue: environment.apiUrl },
  // Error catcher for Sentry
  ErrorRemoteTrackerService.isTrackingAvailable()
    ? {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({
          showDialog: false,
        }),
      }
    : null,
  ErrorRemoteTrackerService.isTrackingAvailable()
    ? {
        provide: Sentry.TraceService,
        deps: [Router],
      }
    : null,
  environment.googleTagManagerId
    ? {
        provide: 'googleTagManagerId',
        useValue: environment.googleTagManagerId,
      }
    : null,
  ProvideValueHelper(MAT_RIPPLE_GLOBAL_OPTIONS, {
    disabled: true,
    animation: {
      enterDuration: 0,
      exitDuration: 0,
    },
  }),
  provideAppInitializer(() => {
    const appInit = inject(AppInitializerService);
    return initializeApp(appInit)();
  }),
  ...MAT_DATE_PROVIDER,
  { provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpOfflineRetryInterceptor,
    multi: true,
  },
  { provide: HTTP_INTERCEPTORS, useClass: JwtRefreshInterceptor, multi: true },
  ProvideFactoryHelper<ITranslocoRootConfig>(
    TRANSLOCO_ROOT_INJECTION_TOKEN,
    (localStorageS: LocalStorageWebService) => {
      const config: ITranslocoRootConfig = {
        isAppVersionChanged: AppVersionService.isVersionChanged,
        setLocalStorageVersion: v => {
          localStorageS.set(LOCAL_STORAGE_KEYS.LOCALIZATION, v);
        },
        getLocalStorageVersion: () => {
          return localStorageS.get(LOCAL_STORAGE_KEYS.LOCALIZATION);
        },
      };
      return config;
    },
    [LocalStorageWebService],
    false,
  ),
  ProvideValueHelper(DICTIONARY_SERVICE_INJECTION_TOKEN, {
    localStorageKeyPrefix: 'tdv-w', // must be with '-w' for avoid reset values for existing clients
    cachedDictionaryTypes: ['countries'],
  }),
  { provide: Sanitizer, useClass: ExtraHtmlSanitizerService },
].filter(i => Boolean(i)) as Array<Provider | EnvironmentProviders>;

const imports: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Array<Array<any> | Type<any> | ModuleWithProviders<any>> | undefined = [
  BrowserModule,
  HttpClientModule,
  BrowserAnimationsModule,
  AppRoutingModule,
  TranslocoRootModule,
  MatDialogModule,
  CdkTrapFocusModule,
  ServiceWorkerModule.register('ngsw-worker.js', {
    enabled: environment.production,
  }),

  // components
  UploadResourceWrapComponent,
  SupportChatComponent,
  GlobalProgressBarComponent,
  AppMenuComponent,
  VersionAvailablePopupComponent,
  LogoutOverlayComponent,
];

@NgModule({
  declarations: [AppComponent],
  imports,
  providers,
  bootstrap: [AppComponent],
})
export class AppModule {}
